import { useState } from 'react'
import {
  LifeBuoy, Mail, MessageCircle, Send, Loader2, CheckCircle2,
  AlertCircle, ChevronDown, HelpCircle,
} from 'lucide-react'
import api from '../../lib/axios'

// ⚠️ Remplace par tes vraies coordonnées
const SUPPORT_EMAIL = 'support@tapio.ma'
const WHATSAPP_NUMBER = '212600000000' // format international, sans + ni espaces

const BTN_PRIMARY =
  'inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#006b5f] text-white text-sm font-semibold ' +
  'hover:bg-[#005a50] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

const CATEGORIES = [
  { value: 'general', label: 'Question générale' },
  { value: 'technical', label: 'Problème technique' },
  { value: 'billing', label: 'Facturation / Abonnement' },
  { value: 'nfc', label: 'Carte NFC / Commande' },
  { value: 'other', label: 'Autre' },
]

const FAQ = [
  {
    q: 'Comment créer ma première page ?',
    a: "Allez dans « Éditeur de Page », choisissez vos blocs (liens, contacts, horaires...), puis cliquez sur « Publier ». Votre page est immédiatement accessible via son lien.",
  },
  {
    q: 'Comment associer une carte NFC à ma page ?',
    a: "Ouvrez « Mes Pages », choisissez la page à lier, puis activez votre carte avec son code. Un simple contact de la carte avec un téléphone ouvre alors votre page.",
  },
  {
    q: 'Comment générer et télécharger un QR code ?',
    a: "Rendez-vous dans « GeneratorQR », saisissez l'adresse de destination, personnalisez le QR code puis téléchargez-le. Chaque scan est comptabilisé dans vos statistiques.",
  },
  {
    q: 'Comment changer ou annuler mon abonnement ?',
    a: "Depuis « Abonnement », vous pouvez changer d'offre ou annuler à tout moment. L'accès Premium reste actif jusqu'à la fin de la période déjà payée.",
  },
  {
    q: 'Où trouver mes factures ?',
    a: "Dans « Commandes & Factures » pour vos commandes, et dans « Abonnement » pour la facturation de votre offre.",
  },
  {
    q: "J'ai oublié mon mot de passe, que faire ?",
    a: "Si vous êtes connecté, changez-le dans « Mon profil ». Sinon, utilisez le lien « Mot de passe oublié » sur la page de connexion.",
  },
]

const errMsg = (e) =>
  e?.response?.data?.message || 'Une erreur est survenue. Réessayez.'


function Alert({ type, children }) {
  if (!children) return null
  const ok = type === 'success'
  const Icon = ok ? CheckCircle2 : AlertCircle
  return (
    <div
      className={`flex items-start gap-2 text-sm rounded-xl px-3 py-2 ${
        ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
      }`}
    >
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  )
}

function Card({ title, icon: Icon, children }) {
  return (
    <section className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 space-y-4">
      <h2 className="flex items-center gap-2 text-base font-semibold text-navy">
        <Icon className="w-5 h-5" /> {title}
      </h2>
      {children}
    </section>
  )
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
      >
        <span>{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && <p className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">{a}</p>}
    </div>
  )
}

export default function Support() {
  const [form, setForm] = useState({ category: 'general', subject: '', message: '' })
  const [state, setState] = useState({ sending: false, ok: '', error: '', errors: {} })

  const submit = async (e) => {
    e.preventDefault()
    setState({ sending: true, ok: '', error: '', errors: {} })
    try {
      const { data } = await api.post('/support', form)
      setForm({ category: 'general', subject: '', message: '' })
      setState({ sending: false, ok: data.message, error: '', errors: {} })
    } catch (err) {
      setState({
        sending: false,
        ok: '',
        error: errMsg(err),
        errors: err?.response?.data?.errors || {},
      })
    }
  }

  const inputCls = (hasError) =>
    `mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[#006b5f]/20 focus:border-[#006b5f] ${
      hasError ? 'border-red-300' : 'border-gray-200'
    }`

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-navy">Aide & support</h1>
        <p className="text-sm text-gray-500">
          Trouvez une réponse rapide ou contactez notre équipe.
        </p>
      </header>

      {/* Contact rapide */}
      <div className="grid sm:grid-cols-2 gap-3">
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 hover:bg-gray-50 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-[#006b5f]/10 text-[#006b5f] flex items-center justify-center">
            <Mail className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy">Email</p>
            <p className="text-xs text-gray-500 truncate">{SUPPORT_EMAIL}</p>
          </div>
        </a>

        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 hover:bg-gray-50 transition-colors"
        >
          <div className="w-10 h-10 rounded-xl bg-[#006b5f]/10 text-[#006b5f] flex items-center justify-center">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy">WhatsApp</p>
            <p className="text-xs text-gray-500">Réponse rapide</p>
          </div>
        </a>
      </div>

      {/* FAQ */}
      <Card title="Questions fréquentes" icon={HelpCircle}>
        <div className="space-y-2">
          {FAQ.map((item) => (
            <FaqItem key={item.q} {...item} />
          ))}
        </div>
      </Card>

      {/* Formulaire */}
      <Card title="Contacter le support" icon={LifeBuoy}>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Catégorie</span>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className={inputCls(state.errors.category)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Sujet</span>
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              maxLength={150}
              required
              className={inputCls(state.errors.subject)}
            />
            {state.errors.subject && (
              <span className="text-xs text-red-600">{state.errors.subject[0]}</span>
            )}
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Message</span>
            <textarea
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Décrivez votre demande avec le plus de détails possible..."
              required
              className={inputCls(state.errors.message)}
            />
            {state.errors.message && (
              <span className="text-xs text-red-600">{state.errors.message[0]}</span>
            )}
          </label>

          <Alert type="success">{state.ok}</Alert>
          <Alert type="error">
            {state.error && !Object.keys(state.errors).length ? state.error : ''}
          </Alert>

          <button type="submit" disabled={state.sending} className={BTN_PRIMARY}>
            {state.sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}{' '}
            Envoyer le message
          </button>
        </form>
      </Card>
    </div>
  )
}